import { Loader } from '../../components/Loader';
import {
  Button,
  ButtonVariants,
  Card,
  Modal,
  Typography,
} from '../../components/primitives';
import Alert from '../../components/ui/Alert';
import { useProjectsContext } from '../../context/projects.context';
import { usePagination } from '../../hooks/pagination.hook';
import { Icons } from '../../icons';
import { testId } from '../../utils';
import { ITEMS_PER_PAGE } from '../../utils/constants';
import { SORT_OPTION_TO_LAST_UPDATED_AT } from '../../utils/project/project-filter';
import { FilterSection } from './FilterSection';
import { GenericProjectCard } from './GenericProjectCard';
import { NoFilteredProjectsCard } from './NoFilteredProjectsCard';
import { Cornerstone } from '@rpi/api';
import { useTranslation } from '@rpi/i18n';
import React, { useMemo } from 'react';

const NoProjectsCard = () => {
  const { t } = useTranslation(undefined, { keyPrefix: 'route.projects' });
  return (
    <Card {...testId('no-projects')}>
      <div className="p-8 space-y-6">
        <div className="space-y-4">
          <Typography as="h3" variant="sub-title">
            {t('no_projects')}
          </Typography>
          <Typography as="p" variant="body">
            {t('no_projects_description')}
          </Typography>
        </div>
        <div>
          <a
            href="https://www.blurb.com/getting-started"
            target="_blank"
            rel="noreferrer"
            className="no-underline"
          >
            <Button {...testId('get-started-button')} size="sm">
              {t('get_started')}
            </Button>
          </a>
        </div>
      </div>
    </Card>
  );
};

const ProjectsList = ({
  projects,
  onOrderMore,
  deleteProject,
}: {
  projects: Cornerstone.ProjectResult[];
  onOrderMore: (project: Cornerstone.ProjectResult) => void;
  deleteProject: (id: number) => void;
}) => {
  const { currentItems, pageRange, pagination, setPageIndex } = usePagination(
    projects,
    ITEMS_PER_PAGE,
    'projects-list',
  );
  return (
    <div className="flex flex-col gap-6">
      <FilterSection pageRange={pageRange} setPageIndex={setPageIndex} />
      {projects.length === 0 && <NoFilteredProjectsCard />}
      {currentItems.map((project) => (
        <GenericProjectCard
          key={project.id}
          project={project}
          onOrderMore={onOrderMore}
          deleteProject={deleteProject}
        />
      ))}
      {pagination}
    </div>
  );
};

const DashboardProjectsList = ({
  projects,
  onOrderMore,
  deleteProject,
}: {
  projects: Cornerstone.ProjectResult[];
  onOrderMore: (project: Cornerstone.ProjectResult) => void;
  deleteProject: (id: number) => void;
}) => {
  const mostRecentProjects = useMemo(
    () => projects.slice().sort(SORT_OPTION_TO_LAST_UPDATED_AT).slice(0, 2),
    [projects],
  );

  return (
    <div className="flex flex-col gap-6">
      {mostRecentProjects.map((project: Cornerstone.ProjectResult) => (
        <GenericProjectCard
          key={project.id}
          project={project}
          onOrderMore={onOrderMore}
          deleteProject={deleteProject}
        />
      ))}
    </div>
  );
};

export const Projects = ({ isDashboard }: { isDashboard?: boolean }) => {
  const {
    projects,
    noProjects,
    projectsLoading,
    deleteLoading,
    isError,
    addToCart,
    disableProject,
    disableProjectMutation,
  } = useProjectsContext();
  const { t } = useTranslation(undefined, { keyPrefix: 'route.projects' });
  const [projectIdToRemove, setProjectIdToRemove] = React.useState<
    number | undefined
  >();

  const onOrderMore = (project: Cornerstone.ProjectResult) => {
    addToCart(project);
  };

  const deleteProject = React.useCallback((projectId: number) => {
    setProjectIdToRemove(projectId);
  }, []);

  const confirmDeletion = React.useCallback(() => {
    if (projectIdToRemove !== undefined) {
      disableProject(projectIdToRemove, () => setProjectIdToRemove(undefined));
    }
  }, [disableProject, projectIdToRemove]);

  if (projectsLoading) {
    return <Loader className="text-brand" variant="bars" size="lg" />;
  }

  if (isError) {
    return (
      <Alert variant="error" {...testId('error')}>
        {t('failed_load')}
      </Alert>
    );
  }

  if (noProjects) {
    return <NoProjectsCard />;
  }
  return (
    <div>
      {isDashboard ? (
        <DashboardProjectsList
          projects={projects}
          onOrderMore={onOrderMore}
          deleteProject={deleteProject}
          {...testId('projects-list')}
        />
      ) : (
        <ProjectsList
          projects={projects}
          onOrderMore={onOrderMore}
          deleteProject={deleteProject}
          {...testId('projects-list')}
        />
      )}
      <Modal
        {...testId('project', 'delete-modal')}
        title={t('delete_project_title')}
        icon={<Icons.WarningIcon />}
        contentElement={
          <div className="flex flex-col gap-">
            <Typography variant="body">
              {t('delete_project_warning')}
            </Typography>
            <br />
            <Typography variant="body">
              {t('delete_project_permanently')}
            </Typography>
          </div>
        }
        closeElement={
          <Button
            {...testId('project', 'delete-modal', 'cancel')}
            onClick={() => setProjectIdToRemove(undefined)}
            variant={ButtonVariants.Secondary}
            size="sm"
          >
            {t('keep_project')}
          </Button>
        }
        serverError={
          disableProjectMutation.error ? t('disable_failed') : undefined
        }
        submitElement={
          <Button
            {...testId('project', 'delete-modal', 'ok')}
            onClick={confirmDeletion}
            loading={deleteLoading}
            size="sm"
          >
            {t('yes_delete')}
          </Button>
        }
        open={projectIdToRemove !== undefined}
      />
    </div>
  );
};
